/**
 * Application shell (§7.8, FR-37 – FR-41).
 *
 * Layout per FR-38: left = ranking board with drafted struck through, centre =
 * top-5 recommendations with score breakdown, right = roster, needs, and
 * upcoming picks, compact footer warnings and source credits.
 *
 * On a phone the three columns stack and a tab bar switches between them,
 * because FR-37 is explicit that a hosted tool gets most of its draft-night
 * traffic from phones rather than second monitors.
 */

import { el, clear, on, fmt, text } from './dom.ts';
import { Store } from '../state/store.ts';
import { loadConfig, decodeConfigUrl, encodeConfigUrl, FOLLOW_THE_LIST_WEIGHTS } from '../state/config.ts';
import { renderOnboarding } from './onboarding.ts';
import { buildBoardRows, renderBoard } from './board.ts';
import { renderDraftBoard } from './draftBoard.ts';
import { renderPlayerDetail, renderRecommendations, renderRoster } from './panels.ts';
import { renderByorModal } from './byorModal.ts';
import { renderCheatSheet, DEFAULT_CHEAT_SHEET_DEPTH } from './cheatsheet.ts';
import { renderDoctorPanel, renderReplayPanel, type ReplayController } from './doctorPanel.ts';
import { renderSettingsPanel } from './settingsPanel.ts';
import { renderMethodPanel } from './methodPanel.ts';
import { renderTutorial } from './tutorial.ts';
import { renderAccountPanel } from './accountPanel.ts';
import { AccountManager } from '../auth/manager.ts';
import { stalenessWarnings } from '../sources/registry.ts';
import { FFC_ATTRIBUTION, FFC_URL } from '../sources/adp.ts';
import { lsGet, lsSet } from '../state/storage.ts';
import { blueprintMetadataTitle, formatBlueprintFreshness } from './blueprintFreshness.ts';
import type { PlayerId } from '../core/types.ts';
import type { AppState } from '../state/store.ts';
import { FANTASY_POSITIONS } from '../core/types.ts';
import { opponentDemandForState, updateOpponentDemandSummary } from './opponentDemand.ts';
import type { OpponentDemandForecast } from '../core/opponentDemand.ts';

type Pane = 'board' | 'recs' | 'roster' | 'draft';
type AppPhase = AppState['phase'];
type Overlay = 'account' | 'byor' | 'doctor' | 'replay' | 'cheatsheet' | 'settings' | 'tutorial' | 'method';

export function mount(root: HTMLElement): Store {
  // A shared config link (FR-65) seeds config before anything else reads it.
  const shared = typeof location !== 'undefined' ? decodeConfigUrl(location.hash) : null;
  const config = { ...loadConfig(), ...(shared ?? {}) };
  const store = new Store(config, { explicitActiveSource: shared?.active_source_id !== undefined });
  const account = new AccountManager();

  let selectedPlayer: PlayerId | null = null;
  let pane: Pane = 'board';
  let modal: Overlay | null = null;
  let mountedModal: Overlay | null = null;
  let replayController: ReplayController | null = null;

  /**
   * A persistent layer for modals and overlays.
   *
   * It is detached and re-attached across board re-renders rather than rebuilt,
   * because several overlays own live state the board knows nothing about — the
   * replay panel holds a loaded draft, a timer, and a playback cursor. Replay
   * drives the board by calling `store.update`, which re-renders; if the
   * overlay were inside the cleared subtree, the first tick would destroy the
   * panel that scheduled it.
   */
  const overlay = el('div', { class: 'overlay-layer' });
  let urlFetchEnabled = true;
  let turnstileSiteKey: string | null = null;

  // FR-71: ask the deployment whether URL mode exists. A static-only deploy
  // (FR-66) answers with a network error, which is correctly read as "no".
  void fetch('/api/health')
    .then((r) => (r.ok ? r.json() : null))
    .then((health: {
      url_fetch_enabled?: boolean;
      turnstile_site_key?: string | null;
      accounts_enabled?: boolean;
      supabase_url?: string | null;
      supabase_publishable_key?: string | null;
      production_origin?: string | null;
    } | null) => {
      urlFetchEnabled = health?.url_fetch_enabled ?? false;
      turnstileSiteKey = health?.turnstile_site_key ?? null;
      void account.initialize({
        enabled: health?.accounts_enabled ?? false,
        supabase_url: health?.supabase_url ?? null,
        supabase_publishable_key: health?.supabase_publishable_key ?? null,
        production_origin: health?.production_origin ?? (typeof location !== 'undefined' ? location.origin : null),
        turnstile_site_key: turnstileSiteKey,
      }, store);
    })
    .catch(() => {
      urlFetchEnabled = false;
      void account.initialize({
        enabled: false,
        supabase_url: null,
        supabase_publishable_key: null,
        production_origin: null,
        turnstile_site_key: null,
      }, store);
    });

  // Onboarding owns its own multi-step DOM and internal step state, so it is
  // mounted once per entry into the phase rather than on every store update.
  // Re-rendering it on every update would reset a visitor to step one the
  // moment anything called `patchConfig` — which the league lookup does,
  // halfway through the flow.
  let mountedPhase: AppPhase | null = null;

  /**
   * The board phase's whole DOM tree, built exactly once and patched in place
   * afterward (FR-39).
   *
   * This app polls Sleeper every few seconds (FR-11), and every poll — even
   * one that changes nothing but the "last updated" timestamp — used to run
   * through `clear(root)` and rebuild the entire shell from scratch: header,
   * footer, both scrollable columns, and the board body all as fresh elements.
   * That defeated `reconcile()` before it ever got to run (its whole job is
   * reusing existing DOM nodes by key, but the container it reconciled into
   * was itself a brand-new, empty node every time) and reset every `.col`'s
   * own scroll position — each one scrolls independently — plus focus on
   * anything a visitor had just clicked, on every single tick. The fix is
   * this: build the persistent nodes once, and every render after that
   * mutates them (text, class lists) rather than replacing them.
   */
  let shell: HTMLElement | null = null;
  let boardBody: HTMLElement | null = null;
  let recsBody: HTMLElement | null = null;
  let opponentDemandEl: HTMLElement | null = null;
  let rosterBody: HTMLElement | null = null;
  let draftBody: HTMLElement | null = null;
  let drawerEl: HTMLElement | null = null;

  let leagueNameEl: HTMLElement | null = null;
  let connectionEl: HTMLElement | null = null;
  let accountButtonEl: HTMLButtonElement | null = null;
  let sortSelect: HTMLSelectElement | null = null;
  let renderedSortSourceKey = '';

  const positionChipEls = new Map<string, HTMLButtonElement>();
  let showDraftedChip: HTMLButtonElement | null = null;
  let compactChip: HTMLButtonElement | null = null;

  // These are deliberately controller-local: polling rebuilds panel contents,
  // but a setup link must continue to describe only the draft configuration.
  let collapsedRecommendationIds = new Set<PlayerId>();
  let rosterDetailsOpen = true;
  let boardDemandOpen = true;
  let draftDemandOpen = true;
  let previousActiveSourceId: string | null = null;

  let vorHeadEl: HTMLElement | null = null;
  let adpHeadEl: HTMLElement | null = null;
  let footerIssuesEl: HTMLElement | null = null;
  let creditsListEl: HTMLElement | null = null;

  const tabEls = new Map<Pane, HTMLButtonElement>();
  const viewButtonEls = new Map<Pane, HTMLButtonElement>();

  const render = () => {
    const state = store.get();
    const phaseChanged = mountedPhase !== state.phase;
    mountedPhase = state.phase;

    if (state.phase !== 'board' && shell) {
      // Leaving the board phase clears root (below, or inside onboarding) —
      // drop the cached refs so a later return to 'board' rebuilds instead of
      // patching nodes that are no longer attached to anything.
      shell = null;
      drawerEl = null;
    }

    if (state.phase === 'onboarding') {
      if (phaseChanged || !root.querySelector('.onboarding')) {
         renderOnboarding(root, store, account, turnstileSiteKey);
      }
      return;
    }

    if (state.phase === 'loading') {
      clear(root);
      root.appendChild(
        el(
          'div',
          { class: 'loading' },
          el(
            'h1',
            { class: 'brand' },
            el('img', { class: 'brand-logo', src: '/logo.png', alt: '', width: '96', height: '64' }),
            'Draft Puppy',
          ),
          el('p', { role: 'status', 'aria-live': 'polite' }, state.loadingMessage || 'Loading…'),
        ),
      );
      return;
    }

    if (state.phase === 'error') {
      clear(root);
      root.appendChild(
        el(
          'div',
          { class: 'error-screen' },
          el('h1', {}, 'Something went wrong'),
          el('p', {}, state.error ?? 'Unknown error.'),
          el(
            'button',
            { class: 'btn btn-primary', type: 'button', onClick: () => location.reload() },
            'Start over',
          ),
        ),
      );
      return;
    }

    if (phaseChanged && modal === null && !lsGet('has_seen_tutorial', false)) {
      modal = 'tutorial';
      lsSet('has_seen_tutorial', true);
    }

    renderBoardPhase(state);
  };

  const buildShell = () => {
    leagueNameEl = el('span', { class: 'league-name' });
    const sourceSwitch = el('div', { class: 'source-switch' }, el('label', { class: 'sort-board-label', for: 'sort-board-select' }, 'Sort board by'));
    connectionEl = el('span', { class: 'connection' });

    sortSelect = el(
      'select',
      {
        id: 'sort-board-select',
        class: 'sort-board-select',
        'aria-label': 'Sort board by',
        title: 'Choose a standard strategy or loaded custom rankings source.',
        onChange: () => {
          if (sortSelect?.value) store.setActiveSource(sortSelect.value);
        },
      },
      el('option', { value: 'blueprint' }, 'Blueprint'),
      el('option', { value: 'value' }, 'Value'),
      el('option', { value: 'blend' }, 'Blend'),
      el('option', { value: 'dynasty' }, 'Dynasty'),
    ) as HTMLSelectElement;
    sourceSwitch.appendChild(sortSelect);

    const viewSwitch = el('div', { class: 'view-switch', role: 'group', 'aria-label': 'Draft views' });
    for (const option of [
      ['board', 'Draft Assistant'],
      ['draft', 'Draft board'],
    ] as const) {
      const [id, label] = option;
      const button = el(
        'button',
        {
          class: 'btn btn-small view-button',
          type: 'button',
          onClick: () => {
            pane = id;
            render();
          },
          'aria-pressed': id === pane,
        },
        label,
      );
      viewButtonEls.set(id, button);
      viewSwitch.appendChild(button);
    }

    accountButtonEl = el('button', {
      class: 'btn btn-small account-button',
      type: 'button',
      onClick: () => { modal = 'account'; render(); },
    }) as HTMLButtonElement;

    // The More popup is positioned against the summary's actual on-screen
    // rect so it stays usable when the header wraps or the source list grows.
    const toolsList = el(
      'div',
      { class: 'more-list' },
      el('button', { class: 'btn btn-small btn-quiet', type: 'button', onClick: () => { modal = 'byor'; render(); } }, '+ Rankings'),
      el('button', { class: 'btn btn-small btn-quiet', type: 'button', onClick: () => { modal = 'method'; render(); } }, 'How it works'),
      el('button', { class: 'btn btn-small btn-quiet', type: 'button', onClick: () => { modal = 'settings'; render(); } }, 'Recommendation settings'),
      el('button', { class: 'btn btn-small btn-quiet', type: 'button', onClick: () => { modal = 'doctor'; render(); } }, 'Preflight check'),
      el('button', { class: 'btn btn-small btn-quiet', type: 'button', onClick: () => { modal = 'cheatsheet'; render(); } }, 'Printable cheat sheet'),
      el('button', { class: 'btn btn-small btn-quiet', type: 'button', onClick: () => { modal = 'replay'; render(); } }, 'Replay a draft'),
      el(
        'button',
        {
          class: 'btn btn-small btn-quiet',
          type: 'button',
          onClick: () => {
            const url = encodeConfigUrl(store.get().config);
            void navigator.clipboard?.writeText(url);
          },
          title: 'Copies a link that reopens this setup elsewhere. Contains no username and no API key.',
        },
        'Copy setup link',
      ),
      accountButtonEl,
    );
    const toolsSummary = el('summary', { class: 'btn btn-small' }, 'More');
    const toolsMenuEl = el('details', { class: 'more-menu' }, toolsSummary, toolsList);

    const positionMoreMenu = () => {
      if (!toolsMenuEl.open) return;
      const margin = 8;
      const anchor = toolsSummary.getBoundingClientRect();
      const { width, height } = toolsList.getBoundingClientRect();
      const left = Math.min(Math.max(margin, anchor.right - width), window.innerWidth - width - margin);
      const top = Math.min(Math.max(margin, anchor.bottom + 6), window.innerHeight - height - margin);
      toolsList.style.left = `${left}px`;
      toolsList.style.top = `${top}px`;
    };
    toolsMenuEl.addEventListener('toggle', positionMoreMenu);
    on(window, 'resize', positionMoreMenu);

    const header = el(
      'header',
      { class: 'topbar' },
      el(
        'span',
        { class: 'brand-row-small' },
        el('img', { class: 'brand-logo-small', src: '/logo.png', alt: '', width: '72', height: '48' }),
        el('span', { class: 'brand-small' }, 'Draft Puppy'),
      ),
      leagueNameEl,
      viewSwitch,
      sourceSwitch,
      connectionEl,
      toolsMenuEl,
    );

    const positions = el('div', { class: 'filter-row', role: 'group', 'aria-label': 'Filter by position' });
    const allChip = el('button', { class: 'chip', type: 'button', onClick: () => setPositionFilter(null) }, 'ALL');
    positionChipEls.set('ALL', allChip);
    positions.appendChild(allChip);
    for (const position of FANTASY_POSITIONS) {
      const chip = el('button', { class: 'chip', type: 'button', onClick: () => setPositionFilter(position) }, position);
      positionChipEls.set(position, chip);
      positions.appendChild(chip);
    }

    showDraftedChip = el('button', {
      class: 'chip',
      type: 'button',
      title: 'Toggle between draftable players and all players, including drafted and season-out players.',
      onClick: () => {
        const s = store.get();
        store.patchConfig({ ui: { ...s.config.ui, show_drafted: !s.config.ui.show_drafted } });
      },
    });
    compactChip = el(
      'button',
      {
        class: 'chip',
        type: 'button',
        // FR-40: the low-bandwidth, minimal-DOM view for bad venue wifi.
        onClick: () => {
          const s = store.get();
          store.patchConfig({ ui: { ...s.config.ui, compact: !s.config.ui.compact } });
        },
      },
      'Compact',
    );

    // The legend lives here rather than in the header row because the header's
    // last column is one of the ones hidden on a narrow screen — which is
    // exactly where it is needed most, since a phone has no hover tooltips.
    const legendChip = el(
      'button',
      {
        class: 'chip chip-quiet',
        type: 'button',
        title: 'What these columns and scores mean',
        onClick: () => { modal = 'method'; render(); },
      },
      '? Numbers & colors',
    );

    const viewOptions = el(
      'details',
      { class: 'view-options' },
      el('summary', { class: 'chip chip-quiet' }, 'View options'),
      el('div', { class: 'view-options-list' }, showDraftedChip, compactChip, legendChip),
    );
    const boardControls = el(
      'div',
      { class: 'board-controls' },
      positions,
      viewOptions,
    );
    opponentDemandEl = el('div', { class: 'opponent-demand-host' });
    boardControls.appendChild(opponentDemandEl);

    // Column headers. These live outside `boardBody` on purpose: `reconcile()`
    // owns that container's children by player key and would drop anything it
    // did not put there. Cell classes are the same ones the rows use, so the
    // responsive rules that hide Tier/Bye/VOR on a narrow screen hide the
    // The last numeric column does double duty: it shows redraft VOR normally,
    // and multi-year dynasty points when the dynasty board is active. Reusing
    // the column rather than adding one keeps a single grid definition — the
    // board's columns are declared in four places across the media queries, and
    // a ninth column would have to be threaded through all of them to show a
    // number that is never useful at the same time as the one already there.
    vorHeadEl = el('span', { class: 'cell vor' }, 'VOR');
    adpHeadEl = el('span', { class: 'cell adp' }, 'ADP');

    // matching headers with them and the two can never disagree.
    const boardHead = el(
      'div',
      // Deliberately *not* `.board-row`: it is not a player row, and anything
      // selecting `.board-row` (tests, keyboard handling, future code) should
      // never pick it up. Column alignment comes from a shared grid rule.
      // `aria-hidden` because it sits outside the `role="table"` body and so
      // cannot be associated with those cells — screen readers get the same
      // information from the per-cell labels on each row instead.
      { class: 'board-head', 'aria-hidden': 'true' },
      el('span', { class: 'cell rank' }, '#'),
      el('span', { class: 'cell name' }, 'Player'),
      el('span', { class: 'cell meta' }, 'Pos'),
      el('span', { class: 'cell tier' }, 'Tier'),
      el('span', { class: 'cell bye' }, 'Bye'),
      adpHeadEl,
      vorHeadEl,
      el('span', { class: 'cell drafted-by' }, 'Drafted'),
    );

    boardBody = el('div', { class: 'board', role: 'table' });
    // Controls and column headers pin to the top of the scrolling board as one
    // unit. Sticking them separately made them overlap — both would pin to
    // `top: 0` and the headers would be drawn over the filter chips.
    const boardCol = el(
      'section',
      { class: 'col col-board', 'aria-label': 'Draft Assistant rankings' },
      el('div', { class: 'board-sticky' }, boardControls, boardHead),
      boardBody,
    );

    recsBody = el('div', { class: 'recs' });
    const recsCol = el(
      'section',
      { class: 'col col-recs', 'aria-label': 'Recommendations' },
      el('h2', {}, 'Take next'),
      recsBody,
    );

    rosterBody = el('div', { class: 'roster' });
    const rosterCol = el('section', { class: 'col col-roster', 'aria-label': 'Your roster' }, rosterBody);

    draftBody = el('div', { class: 'draft-view' });
    const draftCol = el('section', { class: 'col col-draft', 'aria-label': 'Full draft board' }, draftBody);

    footerIssuesEl = el('span', { class: 'footer-issues', role: 'status', 'aria-live': 'polite' });
    creditsListEl = el('div', { class: 'credits-list' });
    const creditsMenu = el(
      'details',
      { class: 'credits-menu' },
      el('summary', { class: 'footer-control' }, 'Sources & credits'),
      creditsListEl,
    );

    const footer = el(
      'footer',
      { class: 'statusbar' },
      footerIssuesEl,
      creditsMenu,
    );

    const tabBar = el(
      'nav',
      { class: 'tabbar', 'aria-label': 'Panels' },
      ...(['board', 'recs', 'roster', 'draft'] as Pane[]).map((p) => {
        const button = el(
          'button',
          {
            class: 'tab',
            type: 'button',
            onClick: () => {
              pane = p;
              render();
            },
          },
          p === 'board' ? 'Board' : p === 'recs' ? 'Take next' : p === 'roster' ? 'Roster' : 'Draft',
        );
        tabEls.set(p, button);
        return button;
      }),
    );

    shell = el('div', { class: 'shell' }, header, boardCol, recsCol, rosterCol, draftCol, footer, tabBar);
    // buildShell only runs once per phase-entry, so this is the one place
    // that needs to clear out whatever the previous phase (loading, error)
    // left behind — every render after this patches `shell` in place instead.
    clear(root);
    root.appendChild(shell);
    root.appendChild(overlay);
  };

  const updateSourceSelector = (state: ReturnType<Store['get']>) => {
    if (!sortSelect) return;
    const key = `${state.blueprintStatus}|${state.sources.map((source) => `${source.source_id}:${source.source_name}`).join('|')}`;
    if (key !== renderedSortSourceKey) {
      renderedSortSourceKey = key;
      clear(sortSelect);
      const blueprintReady = state.blueprintStatus === 'ready';
      const blueprint = el(
        'option',
        {
          value: 'blueprint',
          disabled: !blueprintReady,
          title: blueprintReady
            ? `Research-based forecast mode using model ${state.blueprintSnapshot?.model_version ?? 'current'}.`
            : state.blueprintMessage ?? 'Blueprint is disabled until a valid forecast snapshot is published.',
        },
        blueprintReady ? 'Blueprint' : 'Blueprint (unavailable)',
      );
      sortSelect.append(
        blueprint,
        el('option', { value: 'value' }, 'Value'),
        el('option', { value: 'blend' }, 'Blend'),
        el('option', { value: 'dynasty' }, 'Dynasty'),
      );
      if (state.sources.length > 0) {
        const custom = el('optgroup', { label: 'Custom rankings' });
        for (const source of state.sources) {
          custom.appendChild(el('option', { value: source.source_id }, source.source_name));
        }
        sortSelect.appendChild(custom);
      }
    }
    sortSelect.value = state.config.active_source_id;
    sortSelect.title = state.config.active_source_id === 'blueprint' && state.blueprintStatus !== 'ready'
      ? state.blueprintMessage ?? 'Blueprint is disabled until a valid forecast snapshot is published.'
      : 'Choose a standard strategy or loaded custom rankings source.';
  };

  const updateAccountButton = () => {
    if (!accountButtonEl) return;
    const auth = account.getState().auth;
    const sync = account.getState().sync;
    accountButtonEl.textContent = auth.status === 'signed-in' ? `Account · ${sync}` : 'Sign in to sync';
    accountButtonEl.setAttribute('aria-label', auth.status === 'signed-in' ? 'Open account settings' : 'Sign in to sync saved data');
    accountButtonEl.title = auth.status === 'signed-in' ? 'Account and saved Sleeper profiles' : 'Optional account for saved rankings and setups';
  };

  const updateHeader = (state: ReturnType<Store['get']>) => {
    leagueNameEl!.textContent = state.league
      ? `${state.league.name} · pick #${state.draft?.current_pick_no ?? '—'}`
      : '';

    for (const [id, button] of viewButtonEls) {
      const active = pane === id;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    }

    updateSourceSelector(state);
    updateAccountButton();

    const connection = state.connection;
    const age = state.lastPollAt ? Date.now() - state.lastPollAt : null;
    connectionEl!.className = `connection ${connection}`;
    connectionEl!.title = state.lastPollError ?? '';
    // FR-11: live/stale plus seconds since the last successful poll.
    connectionEl!.textContent =
      connection === 'live'
        ? `live${age !== null ? ` · ${fmt.ago(age)}` : ''}`
        : connection === 'stale'
          ? `stale${age !== null ? ` · ${fmt.ago(age)}` : ''}`
          : connection === 'offline'
            ? 'offline — manual entry still works'
            : 'idle';
  };

  const updateBoardControls = (state: ReturnType<Store['get']>, opponentDemand: OpponentDemandForecast | null) => {
    const filter = state.config.ui.position_filter;
    for (const [key, chip] of positionChipEls) {
      chip.classList.toggle('active', key === 'ALL' ? filter === null : filter === key);
    }
    showDraftedChip!.classList.toggle('active', state.config.ui.show_drafted);
    showDraftedChip!.textContent = state.config.ui.show_drafted ? 'Showing all' : 'Draftable only';
    compactChip!.classList.toggle('active', state.config.ui.compact);

    const dynasty = state.config.active_source_id === 'dynasty';
    const blueprint = state.config.active_source_id === 'blueprint';
    vorHeadEl!.textContent = dynasty ? 'Dyn' : blueprint ? 'Gain' : 'VOR';
    vorHeadEl!.title = dynasty
      ? 'Discounted multi-year points above replacement, rather than this season'
      : blueprint
        ? 'Blueprint marginal lineup gain over the contextual replacement baseline'
        : 'Value over replacement, this season';
    adpHeadEl!.textContent = dynasty ? 'NFL' : 'ADP';
    adpHeadEl!.title = dynasty
      ? 'Where the player went in the NFL draft'
      : 'Average draft position across public drafts';

    if (opponentDemandEl) {
      updateOpponentDemandSummary(opponentDemandEl, opponentDemand, 'compact', {
        open: boardDemandOpen,
        onToggle: (open) => { boardDemandOpen = open; },
      });
    }
  };

  const updateFooter = (state: ReturnType<Store['get']>) => {
    const staleWarnings = stalenessWarnings(state.sources);
    const notices = state.notices.filter((notice) => notice.trim() !== '');
    const adpWarning = !state.adp && !notices.some((notice) => /\badp\b/i.test(notice))
      ? ['No ADP — availability estimates are off.']
      : [];
    const issues = [...new Set([...staleWarnings, ...notices, ...adpWarning])];

    clear(footerIssuesEl!);
    if (issues.length > 0) {
      const label = `${issues.length} data warning${issues.length === 1 ? '' : 's'}`;
      footerIssuesEl!.appendChild(
        el(
          'button',
          {
            class: 'footer-warning',
            type: 'button',
            title: issues.join(' '),
            onClick: () => { modal = 'doctor'; render(); },
          },
          `⚠ ${label}`,
        ),
      );
    }

    clear(creditsListEl!);
    for (const source of state.sources) {
      creditsListEl!.appendChild(
        el(
          'div',
          { class: 'credit-row' },
          source.source_url
            ? el('a', { href: source.source_url, target: '_blank', rel: 'noopener noreferrer' }, source.source_name)
            : el('span', {}, source.source_name),
          el('span', { class: 'credit-date' }, `as of ${source.as_of_date}`),
        ),
      );
    }
    if (state.adp) {
      creditsListEl!.appendChild(
        el(
          'div',
          { class: 'credit-row' },
          el('a', { href: FFC_URL, target: '_blank', rel: 'noopener noreferrer' }, FFC_ATTRIBUTION),
          el('span', { class: 'credit-date' }, `as of ${state.adp.as_of_date}`),
        ),
      );
    }
    if (state.blueprintSnapshot) {
      creditsListEl!.appendChild(
        el(
          'div',
          { class: 'credit-note', title: blueprintMetadataTitle(state.blueprintSnapshot) },
          `Blueprint ${state.blueprintSnapshot.model_version} · ${formatBlueprintFreshness(state.blueprintSnapshot)}`,
        ),
      );
      for (const attribution of state.blueprintSnapshot.attribution) {
        creditsListEl!.appendChild(el('div', { class: 'credit-note' }, attribution));
      }
    }
  };

  const updateTabBar = () => {
    for (const [p, button] of tabEls) button.classList.toggle('active', pane === p);
  };

  const renderBoardPhase = (state: ReturnType<Store['get']>) => {
    if (!shell) buildShell();
    shell!.className = `shell pane-${pane} ${state.config.ui.compact ? 'compact' : ''}`;

    if (previousActiveSourceId !== state.config.active_source_id) {
      collapsedRecommendationIds = new Set();
      previousActiveSourceId = state.config.active_source_id;
    }
    const recommendationIds = state.config.active_source_id === 'blueprint'
      ? state.blueprint?.top.map((rec) => rec.player.player_id_sleeper) ?? []
      : state.engine?.top.map((rec) => rec.player.player_id_sleeper) ?? [];
    collapsedRecommendationIds = new Set(
      [...collapsedRecommendationIds].filter((id) => recommendationIds.includes(id)),
    );

    const opponentDemand = opponentDemandForState(state);
    updateHeader(state);
    updateBoardControls(state, opponentDemand);
    updateFooter(state);
    updateTabBar();

    const rows = buildBoardRows(state, opponentDemand);
    renderBoard(boardBody!, state, rows, {
      onSelect: (id) => {
        selectedPlayer = id;
        renderDrawer();
      },
      onToggleDrafted: (id, drafted) => {
        if (drafted) store.markDrafted(id);
        else store.markUndrafted(id);
      },
    });

    renderRecommendations(
      recsBody!,
      state,
      (id) => {
        selectedPlayer = id;
        renderDrawer();
      },
      opponentDemand,
      {
        collapsedRecommendationIds,
        onRecommendationExplanationToggle: (id, expanded) => {
          if (expanded) collapsedRecommendationIds.delete(id);
          else collapsedRecommendationIds.add(id);
          render();
        },
      },
    );
    renderRoster(rosterBody!, state, {
      detailsOpen: rosterDetailsOpen,
      onDetailsToggle: (open) => { rosterDetailsOpen = open; },
    });
    renderDraftBoard(draftBody!, state, {
      onSelect: (id) => {
        selectedPlayer = id;
        renderDrawer();
      },
      demandOpen: draftDemandOpen,
      onDemandToggle: (open) => { draftDemandOpen = open; },
    }, opponentDemand);

    // Only (re)build an overlay when which overlay is open actually changes.
    // Re-rendering an open panel on every store update would reset whatever
    // the user is part-way through — the mistake that reset onboarding to step
    // one, and that stopped replay dead on its first tick.
    if (mountedModal !== modal) {
      mountedModal = modal;
      clear(overlay);
      if (modal !== null) mountOverlay(state, modal);
    }

    renderDrawer();
  };

  const closeModal = () => {
    replayController?.stop();
    replayController = null;
    modal = null;
    render();
  };

  const mountOverlay = (state: AppState, which: Overlay): void => {
    if (which === 'cheatsheet') {
      // A document rather than a dialog: full-screen, and the print stylesheet
      // needs the app chrome out of the way. Board filters are deliberately
      // ignored — a printed sheet wants every position regardless of what
      // happens to be filtered on screen.
      const host = el('div', { class: 'cheatsheet-host' });
      overlay.appendChild(host);
      renderCheatSheet(
        host,
        state,
        buildBoardRows({
          ...state,
          config: {
            ...state.config,
            ui: { ...state.config.ui, position_filter: null, hide_below_rank: null, show_drafted: true },
          },
        }),
        { perPosition: DEFAULT_CHEAT_SHEET_DEPTH, includeDrafted: false, onClose: closeModal },
      );
      return;
    }

    const host = el('div', { class: 'modal-backdrop' });
    overlay.appendChild(host);

    if (which === 'account') renderAccountPanel(host, account, closeModal, turnstileSiteKey);
    else if (which === 'byor') renderByorModal(host, store, { urlFetchEnabled, turnstileSiteKey, onClose: closeModal });
    else if (which === 'doctor') void renderDoctorPanel(host, state, closeModal);
    else if (which === 'replay') replayController = renderReplayPanel(host, store, closeModal);
    else if (which === 'settings') renderSettingsPanel(host, store, closeModal);
    else if (which === 'method') renderMethodPanel(host, state, closeModal);
    else if (which === 'tutorial') renderTutorial(host, closeModal);
  };

  const renderDrawer = () => {
    if (!selectedPlayer) {
      drawerEl?.remove();
      drawerEl = null;
      return;
    }
    if (!drawerEl) {
      drawerEl = el('aside', { class: 'drawer', role: 'complementary' });
      root.appendChild(drawerEl);
    }
    // `renderPlayerDetail` clears and rebuilds its own contents; keeping the
    // same `<aside>` alive across polls at least preserves the drawer's own
    // scroll position while a visitor stays on the same player.
    renderPlayerDetail(drawerEl, store.get(), store, selectedPlayer, () => {
      selectedPlayer = null;
      renderDrawer();
    });
  };

  const setPositionFilter = (position: string | null) => {
    const state = store.get();
    store.patchConfig({ ui: { ...state.config.ui, position_filter: position } });
  };

  // -------------------------------------------------------------------------
  // Keyboard shortcuts (FR-41)
  // -------------------------------------------------------------------------

  on(document, 'keydown', (event) => {
    const target = event.target as HTMLElement | null;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const state = store.get();
    if (state.phase !== 'board') return;

    // 1–6 filter by position, 0 clears — the second of the three actions used
    // most under time pressure.
    if (/^[0-6]$/.test(event.key)) {
      event.preventDefault();
      const idx = Number(event.key);
      setPositionFilter(idx === 0 ? null : FANTASY_POSITIONS[idx - 1] ?? null);
      return;
    }

    // s cycles the active ranking source.
    if (event.key.toLowerCase() === 's') {
      event.preventDefault();
      const ids = [
        ...state.sources.map((x) => x.source_id),
        'blend',
        'value',
        ...(state.blueprintStatus === 'ready' ? ['blueprint'] : []),
        'dynasty',
      ];
      const current = ids.indexOf(state.config.active_source_id);
      store.setActiveSource(ids[(current + 1) % ids.length] ?? ids[0]!);
      return;
    }

    if (event.key === 'Escape') {
      if (modal) { closeModal(); }
      else if (selectedPlayer) { selectedPlayer = null; renderDrawer(); }
      return;
    }

    if (event.key.toLowerCase() === 'w') {
      event.preventDefault();
      modal = 'settings';
      render();
      return;
    }

    if (event.key.toLowerCase() === 'h') {
      event.preventDefault();
      modal = 'method';
      render();
      return;
    }

    if (event.key === '?') {
      event.preventDefault();
      alert(
        'Shortcuts\n\n' +
          '1–6  filter QB / RB / WR / TE / K / DEF\n' +
          '0    clear position filter\n' +
          's    switch ranking source\n' +
          'd    mark the focused player drafted\n' +
          'w    how it picks (weights)\n' +
          'h    how the numbers work\n' +
          'Esc  close',
      );
    }
  });

  let previousAuthStatus = account.getState().auth.status;
  account.subscribe((next) => {
    const authChanged = next.auth.status !== previousAuthStatus;
    previousAuthStatus = next.auth.status;
    // A signed-in session or a real sign-out changes which profile should seed
    // onboarding. Other account-service updates must not reset a user halfway
    // through typing a guest Sleeper username.
    if (store.get().phase === 'onboarding' && (next.auth.status === 'signed-in' || (authChanged && next.auth.status === 'signed-out'))) {
      mountedPhase = null;
    }
    render();
  });
  store.subscribe(render);
  return store;
}

/** §8 escape hatch, exported so a settings panel can offer it as one click. */
export const followTheListPreset = FOLLOW_THE_LIST_WEIGHTS;
